"use client";

import { useCallback, useState, useRef } from "react";

import AssuranceSection, { type Assurance } from "./AssuranceSection";

const API_BASE = process.env.NEXT_PUBLIC_API_URL || "/api";

interface TransparencyLog {
  entry_id: number;
  merkle_root: string;
  logged_at: string;
}

interface VerificationResponse {
  verified: boolean;
  verification_code?: string;
  envelope_title?: string;
  sender?: { name: string; email: string };
  signer?: { name: string; email: string };
  signed_at?: string;
  original_sha256?: string;
  signed_sha256?: string;
  page_count?: number;
  document_size_bytes?: number;
  hash_match?: boolean;
  method?: string;
  warning?: string;
  message?: string;
  uploaded_sha256?: string;
  transparency_log?: TransparencyLog;
  vc_valid?: boolean;
  /** A specimen: a real signing event on a document that is explicitly NOT
   *  legally binding. Only the /verify/{code} page was taught about this
   *  originally; this upload surface dropped it and rendered the green check. */
  is_specimen?: boolean;
  notice?: string;
  /** HOW the signers were identified — evidence-derived, absent on plain envelopes. */
  assurance?: Assurance;
}

type State = "idle" | "uploading" | "verified" | "not-verified" | "hash-mismatch" | "error";

export default function VerifyUploader() {
  const [state, setState] = useState<State>("idle");
  const [result, setResult] = useState<VerificationResponse | null>(null);
  const [error, setError] = useState("");
  const [dragOver, setDragOver] = useState(false);
  const [fileName, setFileName] = useState("");

  const handleFile = useCallback(async (file: File) => {
    if (!file.name.toLowerCase().endsWith(".pdf")) {
      setError("Please upload a PDF file.");
      setState("error");
      return;
    }
    if (file.size > 15 * 1024 * 1024) {
      setError("File exceeds 15MB limit.");
      setState("error");
      return;
    }

    setFileName(file.name);
    setState("uploading");
    setError("");
    setResult(null);

    try {
      const formData = new FormData();
      formData.append("file", file);

      const res = await fetch(`${API_BASE}/verify/upload`, {
        method: "POST",
        body: formData,
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || `Verification failed (${res.status})`);
      }

      const data: VerificationResponse = await res.json();
      setResult(data);

      if (!data.verified) {
        setState("not-verified");
      } else if (data.hash_match === false) {
        setState("hash-mismatch");
      } else {
        setState("verified");
      }
      window.sfAnalytics?.track(
        "document_verified",
        "Verify",
        data.is_specimen ? "specimen" : data.verified ? "valid" : "invalid",
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : "Verification failed.");
      setState("error");
    }
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragOver(false);
      const file = e.dataTransfer.files[0];
      if (file) handleFile(file);
    },
    [handleFile]
  );

  const handleReset = () => {
    setState("idle");
    setResult(null);
    setError("");
    setFileName("");
  };

  return (
    <div>
      {/* Upload zone */}
      {(state === "idle" || state === "error") && (
        <div
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          className={`relative rounded-xl border-2 border-dashed p-10 text-center transition-colors ${
            dragOver
              ? "border-emerald-400 bg-emerald-50"
              : "border-slate-200 bg-white hover:border-slate-300"
          }`}
        >
          <svg
            className="mx-auto h-10 w-10 text-slate-300 mb-3"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5"
            />
          </svg>
          <p className="text-sm font-medium text-slate-700">
            Drop a signed PDF here, or{" "}
            <label className="cursor-pointer text-emerald-600 hover:text-emerald-700 underline underline-offset-2">
              browse
              <input
                type="file"
                accept=".pdf,application/pdf"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) handleFile(file);
                }}
              />
            </label>
          </p>
          <p className="mt-1 text-xs text-slate-400">PDF up to 15MB</p>
          {error && (
            <p className="mt-3 text-sm text-red-600">{error}</p>
          )}
        </div>
      )}

      {/* Uploading */}
      {state === "uploading" && (
        <div className="rounded-xl border border-slate-200 bg-white p-8 text-center">
          <div className="mx-auto h-8 w-8 animate-spin rounded-full border-4 border-slate-200 border-t-emerald-600" />
          <p className="mt-3 text-sm text-slate-600">
            Verifying <span className="font-medium">{fileName}</span>...
          </p>
        </div>
      )}

      {/* 🔴 Specimen — never the green circled checkmark. That glyph is reserved
          for cryptographic verification and must not appear on a document that
          is not evidence of an agreement. */}
      {state === "verified" && result?.is_specimen && (
        <div
          className="rounded-xl border-2 border-violet-400/70 px-5 py-6 text-center mb-4"
          style={{
            backgroundImage:
              "repeating-linear-gradient(-45deg, rgba(124,58,237,0.07) 0 8px, transparent 8px 16px)",
          }}
        >
          <p className="font-display text-[11px] font-bold uppercase tracking-[0.3em] text-violet-800">
            Specimen
          </p>
          <h3 className="mt-2 font-display text-xl font-bold tracking-tight text-violet-900">
            Not legally binding
          </h3>
          <p className="mx-auto mt-2 max-w-md text-xs leading-relaxed text-violet-900/80">
            {(result.notice ?? "")
              .replace(/^SPECIMEN\s*-\s*NOT LEGALLY BINDING\.\s*/i, "") ||
              "This document was produced by a SignForge specimen run for testing purposes. It is not evidence of an agreement."}
          </p>
          {/* Without this the specimen branch was a dead end: no details, no
              dropzone, no reset — the user had to reload the page. */}
          <button
            type="button"
            onClick={handleReset}
            className="mt-4 text-xs font-medium text-violet-800 underline underline-offset-2 hover:text-violet-900"
          >
            Verify another document
          </button>
        </div>
      )}
      {state === "verified" && result?.is_specimen && (
        <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
          <VerificationDetails result={result} />
        </div>
      )}

      {/* Verified */}
      {state === "verified" && result && !result.is_specimen && (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50/50 overflow-hidden">
          <div className="flex items-center gap-3 px-5 py-4 bg-emerald-100/50 border-b border-emerald-200">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-emerald-500">
              <svg className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
              </svg>
            </div>
            <div>
              <h3 className="text-sm font-bold text-emerald-900">Document Verified</h3>
              <p className="text-xs text-emerald-700">This document was signed through SignForge and its integrity has been confirmed.</p>
            </div>
          </div>
          {(result.vc_valid || result.transparency_log) && (
            <div className="px-5 pt-3 flex items-center gap-2 flex-wrap">
              {result.vc_valid && (
                <span className="inline-flex items-center gap-1 rounded-full bg-blue-50 border border-blue-200 px-2.5 py-0.5 text-[10px] font-semibold text-blue-700">
                  W3C VC 2.0
                </span>
              )}
              {result.transparency_log && (
                <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 border border-emerald-200 px-2.5 py-0.5 text-[10px] font-semibold text-emerald-700">
                  Transparency Log #{result.transparency_log.entry_id}
                </span>
              )}
              <span className="inline-flex items-center gap-1 rounded-full bg-indigo-50 border border-indigo-200 px-2.5 py-0.5 text-[10px] font-semibold text-indigo-700">
                EU Wallet Compatible
              </span>
            </div>
          )}
          <VerificationDetails result={result} />
          <div className="px-5 py-3 border-t border-emerald-200">
            <button onClick={handleReset} className="text-xs font-medium text-emerald-700 hover:text-emerald-800">
              Verify another document
            </button>
          </div>
        </div>
      )}

      {/* Hash mismatch */}
      {state === "hash-mismatch" && result && (
        <div className="rounded-xl border border-amber-200 bg-amber-50/50 overflow-hidden">
          <div className="flex items-center gap-3 px-5 py-4 bg-amber-100/50 border-b border-amber-200">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-amber-500">
              <svg className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
              </svg>
            </div>
            <div>
              <h3 className="text-sm font-bold text-amber-900">Document Modified</h3>
              <p className="text-xs text-amber-700">This document was signed through SignForge but appears to have been modified after signing.</p>
            </div>
          </div>
          {result.warning && (
            <div className="mx-5 mt-4 rounded-lg bg-amber-100 border border-amber-200 px-3 py-2">
              <p className="text-xs text-amber-800">{result.warning}</p>
            </div>
          )}
          <VerificationDetails result={result} />
          <div className="px-5 py-3 border-t border-amber-200">
            <button onClick={handleReset} className="text-xs font-medium text-amber-700 hover:text-amber-800">
              Verify another document
            </button>
          </div>
        </div>
      )}

      {/* Not verified */}
      {state === "not-verified" && (
        <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
          <div className="flex items-center gap-3 px-5 py-4 bg-slate-50 border-b border-slate-200">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-400">
              <svg className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9.75 9.75l4.5 4.5m0-4.5l-4.5 4.5M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <h3 className="text-sm font-bold text-slate-800">Not Signed Through SignForge</h3>
              <p className="text-xs text-slate-500">
                {result?.message || "This document was not signed through SignForge, or the verification record could not be found."}
              </p>
            </div>
          </div>
          <div className="px-5 py-3 border-t border-slate-100">
            <button onClick={handleReset} className="text-xs font-medium text-slate-600 hover:text-slate-800">
              Try another document
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      onClick={() => { navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 1500); }}
      title="Copy to clipboard"
      className="inline-flex items-center gap-1 text-[10px] text-slate-400 hover:text-slate-600 transition-colors"
    >
      {copied ? (
        <svg className="h-3 w-3 text-emerald-500" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" /></svg>
      ) : (
        <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M15.666 3.888A2.25 2.25 0 0013.5 2.25h-3c-1.03 0-1.9.693-2.166 1.638m7.332 0c.055.194.084.4.084.612v0a.75.75 0 01-.75.75H9.75a.75.75 0 01-.75-.75v0c0-.212.03-.418.084-.612m7.332 0c.646.049 1.288.11 1.927.184 1.1.128 1.907 1.077 1.907 2.185V19.5a2.25 2.25 0 01-2.25 2.25H6.75A2.25 2.25 0 014.5 19.5V6.257c0-1.108.806-2.057 1.907-2.185a48.208 48.208 0 011.927-.184" /></svg>
      )}
      {copied ? "Copied" : "Copy"}
    </button>
  );
}

function VerificationDetails({ result }: { result: VerificationResponse }) {
  return (
    <div className="px-5 py-4 space-y-3">
      {result.envelope_title && (
        <DetailRow label="Document" value={result.envelope_title} />
      )}
      {result.sender && (
        <DetailRow label="Sender" value={`${result.sender.name} (${result.sender.email})`} />
      )}
      {result.signer && (
        <DetailRow label="Signer" value={`${result.signer.name} (${result.signer.email})`} />
      )}
      {result.assurance && (
        <div className="flex items-start gap-3">
          <span className="w-[108px] shrink-0 text-xs font-medium text-slate-500">Signer identification</span>
          <div className="flex-1 min-w-0">
            <AssuranceSection assurance={result.assurance} />
          </div>
        </div>
      )}
      {result.signed_at && (
        <DetailRow
          label="Signed at"
          value={new Date(result.signed_at).toLocaleString("en-US", {
            dateStyle: "long",
            timeStyle: "short",
          })}
        />
      )}
      {result.verification_code && (
        <div className="flex items-start gap-3">
          <span className="w-[108px] shrink-0 text-xs font-medium text-slate-500">Verification code</span>
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-800 font-mono">{result.verification_code}</span>
            <CopyButton text={result.verification_code} />
          </div>
        </div>
      )}
      {result.signed_sha256 && (
        <div className="flex items-start gap-3">
          <span className="w-[108px] shrink-0 text-xs font-medium text-slate-500">Signed PDF hash</span>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] text-slate-700 font-mono break-all leading-relaxed">{result.signed_sha256}</p>
            <div className="mt-0.5"><CopyButton text={result.signed_sha256} /></div>
          </div>
        </div>
      )}
      {result.transparency_log && (
        <div className="flex items-start gap-3">
          <span className="w-[108px] shrink-0 text-xs font-medium text-slate-500">Transparency log</span>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold text-emerald-700">
                <svg className="h-2.5 w-2.5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75" />
                </svg>
                Logged
              </span>
              <span className="text-xs text-slate-700">Entry #{result.transparency_log.entry_id}</span>
              <a href="/transparency" target="_blank" className="text-[10px] text-emerald-600 hover:text-emerald-700 underline underline-offset-2">View log</a>
            </div>
            <p className="text-[10px] text-slate-500 font-mono break-all leading-relaxed">
              Root: {result.transparency_log.merkle_root}
            </p>
            <div className="flex items-center gap-3 mt-0.5">
              <CopyButton text={result.transparency_log.merkle_root} />
              {result.transparency_log.logged_at && (
                <span className="text-[10px] text-slate-400">
                  {new Date(result.transparency_log.logged_at).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" })}
                </span>
              )}
            </div>
          </div>
        </div>
      )}
      {result.vc_valid && (
        <div className="flex items-start gap-3">
          <span className="w-[108px] shrink-0 text-xs font-medium text-slate-500">W3C credential</span>
          <div>
            <span className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-2 py-0.5 text-[10px] font-semibold text-blue-700">
              <svg className="h-2.5 w-2.5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75" />
              </svg>
              Valid Verifiable Credential
            </span>
            <p className="text-[10px] text-slate-500 mt-0.5">
              W3C VC 2.0 signing receipt with DataIntegrityProof verified
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

function DetailRow({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <div className="flex items-start gap-3">
      <span className="w-[108px] shrink-0 text-xs font-medium text-slate-500">{label}</span>
      <span className={`text-xs text-slate-800 break-all ${mono ? "font-mono" : ""}`}>
        {value}
      </span>
    </div>
  );
}
